/*
 * Hall Wines — GTM / GA4 data layer runtime
 *
 * Pushes the custom events listed in the spec (navigation_click,
 * social_media_click, cta_click, faq_engagement, plp_filter, plus
 * form-success fires invoked from inline Liquid). Stape's e-commerce
 * events are NOT handled here — Stape's server-side container owns those.
 *
 * Global params (authentication_status, shopify_customer_id,
 * purchaser_type) are read from `window.__hwUser`, populated server-side
 * by `snippets/data-layer-init.liquid` before first paint. We never
 * re-resolve customer state in JS.
 *
 * See: DATA_LAYER_GUIDE.md
 */

const w = /** @type {any} */ (window);
const debugState = {
  enabled: false,
  wrapped: false,
};
const DEBUG_HISTORY_LIMIT = 200;

setupDebugTools();

if (!w.__hwDataLayerBound) {
  w.__hwDataLayerBound = true;

  // Theme-editor guard — avoid polluting the merchant's GA4 property.
  if (!document.documentElement.classList.contains('shopify-design-mode')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind, { once: true });
    } else {
      bind();
    }
  }
}

/**
 * Push a custom event onto window.dataLayer with the global user params
 * re-attached. No-op when `dataLayer` is unavailable.
 *
 * Exposed on window as `__hwPush` so inline form-success scripts and
 * external integrations (review provider, wine-club integration) can
 * fire events without importing this module.
 *
 * @param {string} event
 * @param {Record<string, unknown>} [payload]
 */
function push(event, payload) {
  if (!Array.isArray(w.dataLayer)) return;
  const user = w.__hwUser || {};
  w.dataLayer.push({
    event,
    ...(payload || {}),
    authentication_status: user.user_authentication_status ?? null,
    shopify_customer_id: user.user_shopify_customer_id ?? null,
    purchaser_type: user.user_purchaser_type ?? null,
  });
}

w.__hwPush = push;

function bind() {
  // One delegated click listener routes navigation, social, and CTA.
  document.addEventListener('click', onDocumentClick, false);
  // `toggle` does not bubble — capture phase is required.
  document.addEventListener('toggle', onDocumentToggle, true);
  // PLP facet applies bubble a `change` from the checkbox.
  document.addEventListener('change', onDocumentChange, false);
}

/** @param {MouseEvent} event */
function onDocumentClick(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;

  // Priority order: social → navigation → cta. Each handler returns true
  // when it claims the click, short-circuiting the rest.
  if (handleSocialClick(target)) return;
  if (handleNavigationClick(target)) return;
  if (handleCtaClick(target)) return;
}

/**
 * Resolves a click on a social-media link in the footer.
 * @param {Element} target
 * @returns {boolean} true if the click was claimed by this handler
 */
function handleSocialClick(target) {
  let link = /** @type {HTMLAnchorElement | null} */ (
    target.closest('a[data-social-link]')
  );
  if (!link) {
    link = /** @type {HTMLAnchorElement | null} */ (
      target.closest('.social-icons__wrapper .social-icons__icon-wrapper > a[href]')
    );
  }
  if (!link) return false;

  const platform =
    (link.dataset.socialLink && String(link.dataset.socialLink).trim()) ||
    link.getAttribute('aria-label')?.trim() ||
    textFor(link.querySelector('.social-icons__icon-label'));

  push('social_media_click', {
    social_media_platform: platform,
    click_url: link.href,
  });
  return true;
}

/**
 * Resolves a click on a header / footer navigation link.
 * @param {Element} target
 * @returns {boolean}
 */
function handleNavigationClick(target) {
  const link = /** @type {HTMLAnchorElement | null} */ (target.closest('a'));
  if (!link) return false;
  if (link.closest('[data-social-link]')) return false;
  if (link.closest('.social-icons__wrapper')) return false;
  // CTA-tracked elements are handled by handleCtaClick.
  if (link.closest('[data-cta-track]')) return false;

  const inHeader = link.closest('#header-group');
  const inFooter = link.closest('footer');
  if (!inHeader && !inFooter) return false;

  const parent = link.closest('[data-nav-parent]');
  push('navigation_click', {
    click_text: textFor(link),
    click_url: link.href,
    menu_type: inHeader ? 'header' : 'footer',
    menu_category: parent instanceof HTMLElement ? parent.dataset.navParent || '' : '',
  });
  return true;
}

/**
 * Resolves a click on an opt-in tracked CTA inside the page body.
 * @param {Element} target
 * @returns {boolean}
 */
function handleCtaClick(target) {
  const cta = /** @type {HTMLElement | null} */ (target.closest('[data-cta-track]'));
  if (!cta) return false;
  if (!cta.closest('main, #MainContent, [role="main"]')) return false;

  const url =
    cta instanceof HTMLAnchorElement
      ? cta.href
      : cta.dataset.ctaUrl || '';

  push('cta_click', {
    click_text: textFor(cta),
    click_url: url,
    section_heading: resolveSectionHeading(cta),
  });
  return true;
}

/**
 * Resolves a filter application on a PLP (collection / search) facet.
 * Fires only when a facet value is *applied* (checked), not when removed,
 * matching the spec's "user applies a filter". Price (range) and sort are
 * not `facet-inputs-component` checkboxes, so they're naturally excluded.
 * @param {Event} event
 */
function onDocumentChange(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'checkbox' || !target.checked) return;

  const group = target.closest('facet-inputs-component');
  if (!(group instanceof HTMLElement)) return;

  push('plp_filter', {
    filter_category: group.dataset.filterCategory || '',
    filter_value: target.dataset.label || target.getAttribute('aria-label') || '',
  });
}

/** @param {ToggleEvent} event */
function onDocumentToggle(event) {
  const target = /** @type {EventTarget | null} */ (event.target);
  if (!(target instanceof HTMLDetailsElement)) return;
  if (!target.open) return;
  if (!target.closest('[data-faq-page]')) return;

  const summary = target.querySelector('summary');
  push('faq_engagement', {
    faq_question: textFor(summary),
  });
}

/**
 * Visible text content for a tracked element. Falls back to the element's
 * `aria-label` or `title` attribute when textContent is empty (icon-only
 * CTAs and the like).
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function textFor(el) {
  if (!el) return '';
  const txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (txt) return txt;
  if (el instanceof HTMLElement) {
    return el.getAttribute('aria-label')?.trim() || el.title?.trim() || '';
  }
  return '';
}

/**
 * Resolve CTA section heading: explicit `data-cta-section` first, then the
 * nearest preceding heading (`data-cta-heading`, h1–h6) within the slide/group,
 * then within the Shopify section.
 * @param {HTMLElement} cta
 * @returns {string}
 */
function resolveSectionHeading(cta) {
  const explicitSection = cta.closest('[data-cta-section]');
  if (explicitSection instanceof HTMLElement) {
    const explicit = explicitSection.dataset.ctaSection || '';
    if (explicit) return explicit;
  }

  const localRoot = cta.closest('.slide, .group-block');
  if (localRoot instanceof HTMLElement) {
    const local = findClosestPrecedingHeading(cta, localRoot);
    if (local) return local;
  }

  return findClosestPrecedingHeading(
    cta,
    cta.closest('.shopify-section, main, #MainContent, [role="main"]')
  );
}

/**
 * @param {HTMLElement} from
 * @param {Element | null} root
 * @returns {string}
 */
function findClosestPrecedingHeading(from, root) {
  if (!root) return '';

  const candidates = root.querySelectorAll(
    'h1, h2, h3, h4, h5, h6, [data-cta-heading]'
  );
  let best = '';

  for (const el of candidates) {
    if (!(el instanceof HTMLElement)) continue;
    if (el === from || from.contains(el)) continue;
    if (!(from.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING)) continue;

    const headingText = el.dataset.ctaHeading?.trim() || textFor(el);
    if (headingText) best = headingText;
  }

  return best;
}

function setupDebugTools() {
  if (!Array.isArray(w.__hwDataLayerEvents)) {
    w.__hwDataLayerEvents = [];
  }

  debugState.enabled = isDebugEnabled();
  wrapDataLayerPush();

  w.__hwDataLayerDebug = {
    enable() {
      debugState.enabled = true;
      try {
        w.localStorage.setItem('hwDataLayerDebug', '1');
      } catch (_) {}
      console.info('[HW data-layer] debug enabled');
    },
    disable() {
      debugState.enabled = false;
      try {
        w.localStorage.removeItem('hwDataLayerDebug');
      } catch (_) {}
      console.info('[HW data-layer] debug disabled');
    },
    state() {
      return {
        enabled: debugState.enabled,
        eventsBuffered: w.__hwDataLayerEvents.length,
      };
    },
    dump() {
      return w.__hwDataLayerEvents.slice();
    },
    clear() {
      w.__hwDataLayerEvents = [];
      console.info('[HW data-layer] debug buffer cleared');
    },
  };

  if (debugState.enabled) {
    console.info('[HW data-layer] debug logging is enabled');
  }
}

function wrapDataLayerPush() {
  if (debugState.wrapped) return;
  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }
  if (typeof w.dataLayer.push !== 'function') return;

  const nativePush = w.dataLayer.push.bind(w.dataLayer);
  w.dataLayer.push = function wrappedPush(...entries) {
    for (const entry of entries) {
      recordDataLayerEntry(entry);
    }
    return nativePush(...entries);
  };

  debugState.wrapped = true;
}

/**
 * @param {unknown} entry
 */
function recordDataLayerEntry(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;

  const eventEntry = /** @type {Record<string, unknown>} */ (entry);
  const snapshot = {
    timestamp: new Date().toISOString(),
    ...eventEntry,
  };

  w.__hwDataLayerEvents.push(snapshot);
  if (w.__hwDataLayerEvents.length > DEBUG_HISTORY_LIMIT) {
    w.__hwDataLayerEvents.shift();
  }

  if (debugState.enabled) {
    const label = typeof eventEntry.event === 'string' ? eventEntry.event : '(no event name)';
    console.log('[HW data-layer]', label, snapshot);
  }
}

function isDebugEnabled() {
  if (w.__HW_DATA_LAYER_DEBUG === true) return true;
  try {
    if (w.localStorage.getItem('hwDataLayerDebug') === '1') return true;
  } catch (_) {}
  const params = new URLSearchParams(w.location.search);
  return params.get('dl_debug') === '1';
}
